import type { ApiEnvelope, ApiFailure, ApiMeta } from "../types";
import { isRecord, stringValue } from "../types";

const configuredBase = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
const API_BASE = configuredBase.endsWith("/") ? configuredBase.slice(0, -1) : configuredBase;

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(options: { code: string; message: string; status: number; details?: unknown; requestId?: string }) {
    super(options.message);
    this.name = "ApiError";
    this.code = options.code;
    this.status = options.status;
    this.details = options.details;
    this.requestId = options.requestId;
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: BodyInit | Record<string, unknown> | undefined;
  idempotencyKey?: string;
  raw?: boolean;
  onUploadProgress?: (percentage: number) => void;
}

function csrfToken(): string | undefined {
  const match = document.cookie.split("; ").find((item) => item.startsWith("XSRF-TOKEN="));
  return match === undefined ? undefined : decodeURIComponent(match.slice("XSRF-TOKEN=".length));
}

function isBodyInit(value: unknown): value is BodyInit {
  return typeof value === "string" || value instanceof FormData || value instanceof URLSearchParams || value instanceof Blob || value instanceof ArrayBuffer;
}

function normalizedPath(path: string): string {
  if (/^https?:\/\//u.test(path)) return path;
  if (path.startsWith("/api/")) return path;
  return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

function buildHeaders(options: RequestOptions, method: string): Headers {
  const headers = new Headers(options.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  const body = options.body;
  if (body !== undefined && !(body instanceof FormData) && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    headers.set("Idempotency-Key", options.idempotencyKey ?? crypto.randomUUID());
    const csrf = csrfToken();
    if (csrf !== undefined) headers.set("X-CSRF-Token", csrf);
  }
  return headers;
}

async function parseResponse(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) return response.json();
  if (contentType.includes("application/octet-stream") || contentType.includes("application/gzip") || contentType.includes("application/x-tar") || contentType.includes("application/zip") || contentType.includes("application/pdf")) return response.blob();
  const text = await response.text();
  return text.length === 0 ? null : text;
}

function failureFromUnknown(payload: unknown): ApiFailure | undefined {
  if (!isRecord(payload) || payload["ok"] !== false || !isRecord(payload["error"])) return undefined;
  const error = payload["error"];
  const requestId = isRecord(payload["meta"]) ? stringValue(payload["meta"]["requestId"], "") : "";
  return {
    ok: false,
    error: {
      code: stringValue(error["code"], "INTERNAL_ERROR"),
      message: stringValue(error["message"], "Request failed"),
      ...(error["details"] !== undefined ? { details: error["details"] } : {}),
    },
    ...(requestId !== "" ? { meta: { requestId } } : {}),
  };
}

function throwFailure(response: Response, payload: unknown): never {
  const failure = failureFromUnknown(payload);
  if (failure !== undefined) {
    throw new ApiError({
      code: failure.error.code,
      message: failure.error.message,
      status: response.status,
      ...(failure.error.details !== undefined ? { details: failure.error.details } : {}),
      ...(failure.meta?.requestId !== undefined ? { requestId: failure.meta.requestId } : {}),
    });
  }
  throw new ApiError({
    code: response.status === 401 ? "AUTH_SESSION_REQUIRED" : response.status === 403 ? "AUTH_PERMISSION_DENIED" : response.status === 404 ? "RESOURCE_NOT_FOUND" : response.status === 429 ? "RATE_LIMITED" : response.status >= 500 ? "SERVICE_UNAVAILABLE" : "INTERNAL_ERROR",
    message: typeof payload === "string" ? payload : response.statusText,
    status: response.status,
  });
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = buildHeaders(options, method);
  let body: BodyInit | undefined;
  if (options.body !== undefined) body = isBodyInit(options.body) ? options.body : JSON.stringify(options.body);
  const { body: _configuredBody, idempotencyKey: _idempotencyKey, raw: _raw, onUploadProgress: _onUploadProgress, ...fetchOptions } = options;
  let response: Response;
  try {
    response = await fetch(normalizedPath(path), { ...fetchOptions, method, headers, ...(body !== undefined ? { body } : {}), credentials: "include" });
  } catch (cause) {
    throw new ApiError({ code: "SERVICE_UNAVAILABLE", message: cause instanceof Error ? cause.message : "Network request failed", status: 0, details: cause });
  }
  const payload = await parseResponse(response);
  if (!response.ok) throwFailure(response, payload);
  if (options.raw === true) return payload as T;
  const failure = failureFromUnknown(payload);
  if (failure !== undefined) throwFailure(response, payload);
  if (isRecord(payload) && payload["ok"] === true && "data" in payload) return payload["data"] as T;
  return payload as T;
}

export const api = {
  request,
  get: <T>(path: string, options: RequestOptions = {}) => request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: RequestOptions["body"], options: RequestOptions = {}) => request<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body?: RequestOptions["body"], options: RequestOptions = {}) => request<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(path: string, body?: RequestOptions["body"], options: RequestOptions = {}) => request<T>(path, { ...options, method: "PATCH", body }),
  delete: <T>(path: string, body?: RequestOptions["body"], options: RequestOptions = {}) => request<T>(path, { ...options, method: "DELETE", body }),
};

export function asApiError(value: unknown): ApiError {
  if (value instanceof ApiError) return value;
  return new ApiError({ code: "INTERNAL_ERROR", message: value instanceof Error ? value.message : "Unexpected error", status: 0, details: value });
}

export type { ApiEnvelope, ApiMeta };
